import React from "react";
import { Loader2, Mail } from "lucide-react";
import { useFormContext } from "react-hook-form";
import type { CombinedFormData } from "@/components/login/login-schema";
import { ContactButton } from "@/components/common/contact-button";
import { PolicyRow } from "@/components/login/policy-row";
import { TERMS_AGREEMENT_ITEMS } from "@/constants/terms";
import { useShake } from "@/components/login/use-shake";
import { useCapsLock } from "@/hooks/use-caps-lock";
import { auth } from "@/lib/firebase";
import { cn } from "@/lib/utils";

export const PasswordReset: React.FC = () => {
  const {
    watch,
    register,
    setValue,
    formState: { errors, isValid, isSubmitting },
    trigger,
  } = useFormContext<CombinedFormData>();

  const [newPassword, confirmPassword, agreeTerms, agreePrivacy] = watch([
    "newPassword",
    "confirmNewPassword",
    "agreeTerms",
    "agreePrivacy",
  ]);

  React.useEffect(() => {
    if (newPassword || confirmPassword) {
      trigger(["newPassword", "confirmNewPassword"]);
    }
  }, [newPassword, confirmPassword, trigger]);

  const { isShaking, triggerShake } = useShake();
  const capsLockOn = useCapsLock();

  const currentUser = auth.currentUser;
  const userEmail = currentUser?.email ?? "";
  const userLabel = currentUser?.displayName
    ? `${currentUser.displayName}(${userEmail})`
    : userEmail;

  const canSubmit = isValid && agreeTerms && agreePrivacy && !isSubmitting;

  const inputClassName = (hasError: boolean) =>
    `w-full h-9 px-3 py-1 text-[14px] rounded-lg border outline-none bg-white placeholder:text-neutral-500 ${
      hasError ? "border-red-600" : "border-neutral-200"
    } shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]`;

  return (
    <div className="flex flex-col gap-6 items-center w-full max-w-[374px] rounded-[10px]">
      {/* Header */}
      <div className="flex flex-col items-center gap-4 w-full text-center">
        <h1 className="font-['Pretendard_Variable'] font-bold text-[24px] text-primary tracking-[-0.552px] w-full">
          계정 활성화
        </h1>
        <p className="text-[14px] leading-5 text-label-alternative">
          새로운 비밀번호를 설정하고
          <br />
          약관에 동의하면 바로 시작할 수 있습니다.
        </p>
      </div>

      {/* User info */}
      {userEmail && (
        <div className="flex w-full items-center justify-center gap-2 px-4 py-3 rounded-lg bg-background-gray-alternative">
          <Mail className="w-[18px] h-[18px] shrink-0 text-blue-500" />
          <span className="text-[12px] font-medium text-primary truncate">
            {userLabel}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-10 w-full">
        <div className="flex flex-col gap-6 w-full">
          {/* New Password Input */}
          <div className="flex flex-col gap-2">
            <label className="font-['Pretendard_Variable'] font-medium text-[14px] text-primary">
              비밀번호
            </label>
            <input
              type="password"
              placeholder="비밀번호(영문, 숫자 포함 8~16자)"
              className={inputClassName(!!(newPassword && errors.newPassword))}
              {...register("newPassword")}
            />
            {newPassword && errors.newPassword && (
              <p className="text-[12px] text-status-error">
                {errors.newPassword.message}
              </p>
            )}
          </div>

          {/* Confirm Password Input */}
          <div className="flex flex-col gap-2">
            <label className="font-['Pretendard_Variable'] font-medium text-[14px] text-primary">
              비밀번호 확인
            </label>
            <input
              type="password"
              placeholder="비밀번호 재입력"
              className={inputClassName(
                !!(confirmPassword && errors.confirmNewPassword),
              )}
              {...register("confirmNewPassword")}
            />
            {confirmPassword && errors.confirmNewPassword && (
              <p className="text-[12px] text-status-error">
                {errors.confirmNewPassword.message}
              </p>
            )}
          </div>

          {/* Caps Lock은 두 비밀번호 필드에 공통이므로 경고는 한 번만 표시 */}
          {capsLockOn && (
            <p role="alert" className="-mt-4 text-[12px] text-status-error">
              Caps Lock이 켜져 있습니다.
            </p>
          )}

          {/* Terms agreement */}
          <div className="flex flex-col gap-1 w-full">
            <PolicyRow
              label={TERMS_AGREEMENT_ITEMS.service.label}
              checked={!!agreeTerms}
              onToggle={() =>
                setValue("agreeTerms", !agreeTerms, { shouldValidate: true })
              }
              viewUrl={TERMS_AGREEMENT_ITEMS.service.url}
              shake={isShaking && !agreeTerms}
            />
            <PolicyRow
              label={TERMS_AGREEMENT_ITEMS.privacy.label}
              checked={!!agreePrivacy}
              onToggle={() =>
                setValue("agreePrivacy", !agreePrivacy, { shouldValidate: true })
              }
              viewUrl={TERMS_AGREEMENT_ITEMS.privacy.url}
              shake={isShaking && !agreePrivacy}
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!isValid || isSubmitting}
          onClick={(e) => {
            if (!agreeTerms || !agreePrivacy) {
              e.preventDefault();
              triggerShake();
            }
          }}
          className={cn(
            "w-full h-10 px-3 rounded-[6px] bg-primary-base text-static-white text-[15px] font-medium disabled:cursor-not-allowed flex items-center justify-center",
            !canSubmit && "opacity-24",
          )}
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-static-white" />
              <span>활성화 중...</span>
            </div>
          ) : (
            "시작하기"
          )}
        </button>

        {/* Footer */}
        <div className="flex items-center justify-center gap-3 w-full">
          <span className="text-[14px] font-medium text-label-normal">
            문제가 생겼나요?
          </span>
          <ContactButton className="!text-label-normal" />
        </div>
      </div>
    </div>
  );
};
